import React, { useState, useEffect, Suspense, useCallback, useRef } from 'react';
import { TabContent } from '@/components/ui/tab-content';
import { 
  SidebarProvider, 
  Sidebar, 
  SidebarHeader, 
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  SidebarInset,
  SidebarGroup,
  SidebarGroupLabel
} from "@/components/ui/sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  FileText, 
  Settings, 
  Briefcase, 
  DollarSign, 
  PieChart, 
  TrendingUp, 
  MessageSquare, 
  ChevronLeft, 
  LayoutDashboard,
  Users,
  Home,
  User,
  Bell,
  Building,
  ListTodo,
  Calendar,
  UserPlus
} from 'lucide-react';
import { Link } from "react-router-dom";
import { useAuth } from '@/contexts/AuthContext';
import PlatformMetricsManager from "@/components/admin/PlatformMetricsManager";
import BusinessProgressManager from "@/components/admin/BusinessProgressManager";
import FundingRequirementsManager from "@/components/admin/FundingRequirementsManager";
import DocumentManager from "@/components/admin/DocumentManager";
import SectorAllocationManager from "@/components/admin/SectorAllocationManager";
import PerformanceDataManager from "@/components/admin/PerformanceDataManager";
import ContactMessageManager from '@/components/admin/ContactMessageManager';
import CompanyManager from '@/components/admin/CompanyManager';
import UsersManager from '@/components/admin/UsersManager';
import SettingsManager from '@/components/admin/SettingsManager';
import { KanbanBoard } from '@/components/admin/KanbanBoard';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorBoundary } from 'react-error-boundary';
import { WizardInfoManager } from '@/components/admin/WizardInfoManager';

interface Meeting {
  id: string;
  business_id: string;
  investor_id: string;
  meeting_date: string;
  meeting_time: string;
  full_name: string;
  email: string;
  phone: string;
  investment_amount: number;
  topic: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  created_at: string;
  business: {
    business_name: string;
  };
}

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <Card className="w-[600px]">
        <CardHeader>
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>We encountered an error while loading the admin portal</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600 mb-4">{error.message}</p>
          <Button onClick={resetErrorBoundary}>Try again</Button>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('adminActiveTab');
    return savedTab || "platform";
  });
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const componentMounted = useRef(true);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          if (componentMounted.current) {
            setIsAuthenticated(false);
          }
          return;
        }

        // Check if user is admin
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('user_id', session.user.id)
          .single();

        if (componentMounted.current) {
          setIsAuthenticated(!!session && profile?.user_type === 'admin');
        }

        if (!profile || profile.user_type !== 'admin') {
          toast.error('You do not have permission to access the admin portal');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (componentMounted.current) {
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session) {
        if (componentMounted.current) {
          setIsAuthenticated(false);
        }
        return;
      }

      // Check if user is admin on auth state change
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('user_id', session.user.id)
        .single();

      if (componentMounted.current) {
        setIsAuthenticated(!!session && profile?.user_type === 'admin');
      }
    });

    return () => {
      componentMounted.current = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Memoized tab change handler
  const handleTabChange = useCallback((value: string) => {
    if (!isAuthenticated) return;
    setActiveTab(value);
    localStorage.setItem('adminActiveTab', value);
  }, [isAuthenticated]);

  // Reset state on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  // Fetch meetings with auth check
  const fetchMeetings = useCallback(async () => {
    if (!isAuthenticated || !componentMounted.current) return;
    
    try {
      setIsLoading(true);
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.user) {
        toast.error('Please log in to view meetings');
        return;
      }

      // First check if user has admin access
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('user_id', session.session.user.id)
        .single();

      if (!componentMounted.current) return;

      if (profileError || profile?.user_type !== 'admin') {
        toast.error('You do not have permission to view meetings');
        return;
      }

      // Fetch meetings first
      const { data: meetingsData, error: meetingsError } = await supabase
        .from('meetings')
        .select('*')
        .order('created_at', { ascending: false });

      if (!componentMounted.current) return;

      if (meetingsError) {
        console.error('Error fetching meetings:', meetingsError);
        toast.error('Failed to load meetings: ' + meetingsError.message);
        return;
      }

      if (!meetingsData) {
        toast.error('No meetings data found');
        return;
      }

      // Fetch all related businesses
      const businessIds = meetingsData.map(m => m.business_id);
      const { data: businessesData, error: businessesError } = await supabase
        .from('businesses')
        .select('id, business_name')
        .in('id', businessIds);

      if (!componentMounted.current) return;

      if (businessesError) {
        console.error('Error fetching businesses:', businessesError);
        toast.error('Failed to load business details');
        return;
      }

      // Create a map of business data
      const businessMap = (businessesData || []).reduce((acc, business) => {
        acc[business.id] = business;
        return acc;
      }, {} as Record<string, { id: string; business_name: string }>);

      // Combine the data
      const meetings = meetingsData.map(meeting => ({
        ...meeting,
        business: businessMap[meeting.business_id] || { business_name: 'Unknown Business' }
      }));

      if (componentMounted.current) {
        setMeetings(meetings);
      }
    } catch (error) {
      console.error('Error:', error);
      if (componentMounted.current) {
        toast.error('An unexpected error occurred while loading meetings');
      }
    } finally {
      if (componentMounted.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  // Fetch meetings when tab changes
  useEffect(() => {
    if (activeTab === "meetings" && isAuthenticated) {
      fetchMeetings();
    }
  }, [activeTab, isAuthenticated, fetchMeetings]);

  const updateMeetingStatus = async (meetingId: string, newStatus: Meeting['status']) => {
    try {
      const { error } = await supabase
        .from('meetings')
        .update({ status: newStatus })
        .eq('id', meetingId);

      if (error) {
        console.error('Error updating meeting status:', error);
        toast.error('Failed to update meeting status');
        return;
      }

      toast.success('Meeting status updated');
      fetchMeetings(); // Refresh the list
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update meeting status');
    }
  };

  const getStatusBadgeColor = (status: Meeting['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to access the admin portal</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.href = '/login'}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        setActiveTab("platform");
        setMeetings([]);
        setIsLoading(true);
      }}
    >
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <header className="border-b border-border bg-background py-2 px-4 flex items-center justify-between h-14 z-10">
          <div className="flex items-center gap-3">
            <img src="/uploads/d7bc4401-0a39-47ae-a0ca-769ce1b8ba37.png" alt="H44S Logo" className="h-10" />
            <span className="font-semibold text-foreground text-sm">Admin Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <Bell size={20} />
            </Button>
            <Avatar className="h-8 w-8">
              <AvatarImage src="" alt={user?.email || "User"} />
              <AvatarFallback className="bg-h44s-navy text-white">
                {user?.email?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        <div className="flex flex-1 w-full">
          <Sidebar className="border-r border-border bg-card">
            <SidebarHeader className="pt-0 px-2">
              <div className="flex items-center justify-between px-2 h-12">
                <span className="text-sm font-medium">Administration</span>
                <SidebarTrigger />
              </div>
            </SidebarHeader>
            <SidebarContent className="px-2">
              <SidebarMenu>
                <SidebarMenuItem>
                  <Link to="/dashboard">
                    <SidebarMenuButton tooltip="Dashboard" className="text-foreground hover:bg-secondary">
                      <Home className="w-4 h-4" />
                      <span>Dashboard</span>
                    </SidebarMenuButton>
                  </Link>
                </SidebarMenuItem>

                <SidebarGroup>
                  <SidebarGroupLabel className="text-muted-foreground">Analytics</SidebarGroupLabel>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "platform"} 
                      onClick={() => handleTabChange("platform")}
                      tooltip="Platform Metrics"
                      className="text-foreground hover:bg-secondary data-[active=true]:bg-primary/10 data-[active=true]:text-primary"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span>Platform Metrics</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "performance"} 
                      onClick={() => handleTabChange("performance")}
                      tooltip="Performance"
                    >
                      <TrendingUp className="w-4 h-4" />
                      <span>Performance</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "sectors"} 
                      onClick={() => handleTabChange("sectors")}
                      tooltip="Sector Allocation"
                    >
                      <PieChart className="w-4 h-4" />
                      <span>Sector Allocation</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarGroup>

                <SidebarGroup>
                  <SidebarGroupLabel className="text-muted-foreground">Management</SidebarGroupLabel>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "business"} 
                      onClick={() => handleTabChange("business")}
                      tooltip="Business Progress"
                    >
                      <Briefcase className="w-4 h-4" />
                      <span>Business Progress</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "funding"} 
                      onClick={() => handleTabChange("funding")}
                      tooltip="Funding Requirements"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Funding Requirements</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "documents"} 
                      onClick={() => handleTabChange("documents")}
                      tooltip="Document Management"
                    >
                      <FileText className="w-4 h-4" />
                      <span>Document Management</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "tasks"} 
                      onClick={() => handleTabChange("tasks")}
                      tooltip="Task Management"
                    >
                      <ListTodo className="w-4 h-4" />
                      <span>Task Management</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarGroup>

                <SidebarGroup>
                  <SidebarGroupLabel className="text-muted-foreground">Communication</SidebarGroupLabel>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "contacts"} 
                      onClick={() => handleTabChange("contacts")}
                      tooltip="Contact Messages"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>Contact Messages</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "meetings"} 
                      onClick={() => handleTabChange("meetings")}
                      tooltip="Meeting Requests"
                    >
                      <Calendar className="w-4 h-4" />
                      <span>Meeting Requests</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarGroup>

                <SidebarGroup>
                  <SidebarGroupLabel className="text-muted-foreground">System</SidebarGroupLabel>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "companies"} 
                      onClick={() => handleTabChange("companies")}
                      tooltip="Company Management"
                    >
                      <Building className="w-4 h-4" />
                      <span>Company Management</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "users"} 
                      onClick={() => handleTabChange("users")}
                      tooltip="User Management"
                    >
                      <Users className="w-4 h-4" />
                      <span>User Management</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "settings"} 
                      onClick={() => handleTabChange("settings")}
                      tooltip="Settings"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      isActive={activeTab === "wizard"} 
                      onClick={() => handleTabChange("wizard")}
                      tooltip="Onboarding Information"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Onboarding Info</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarGroup>
              </SidebarMenu>
            </SidebarContent>
          </Sidebar>

          <div className="flex-1 bg-gray-50 p-6">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {activeTab === "platform" && "Platform Metrics"}
                {activeTab === "business" && "Business Progress"}
                {activeTab === "funding" && "Funding Requirements"}
                {activeTab === "documents" && "Document Management"}
                {activeTab === "sectors" && "Sector Allocation"}
                {activeTab === "performance" && "Performance Data"}
                {activeTab === "contacts" && "Conversations"}
                {activeTab === "companies" && "Company Pitch Decks"}
                {activeTab === "users" && "User Management"}
                {activeTab === "settings" && "Platform Settings"}
                {activeTab === "tasks" && "Task Management"}
                {activeTab === "wizard" && "Onboarding Information"}
              </h1>
              <p className="text-muted-foreground mt-1">
                {activeTab === "platform" && "Track key platform metrics and KPIs"}
                {activeTab === "business" && "Monitor business development progress"}
                {activeTab === "funding" && "Manage funding requirements and allocations"}
                {activeTab === "documents" && "Manage investor documents and approvals"}
                {activeTab === "sectors" && "Configure investment sector allocations"}
                {activeTab === "performance" && "Track investment performance over time"}
                {activeTab === "contacts" && "Manage all contact form submissions and inquiries"}
                {activeTab === "companies" && "Manage company pitch decks and profiles"}
                {activeTab === "users" && "Manage user accounts and permissions"}
                {activeTab === "settings" && "Configure global platform settings"}
                {activeTab === "tasks" && "Manage and track tasks across the platform"}
                {activeTab === "wizard" && "View onboarding wizard information for investors and entrepreneurs"}
              </p>
            </div>

            <div className="w-full">
              <TabContent value="platform" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <PlatformMetricsManager />
                </Suspense>
              </TabContent>

              <TabContent value="business" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <BusinessProgressManager />
                </Suspense>
              </TabContent>

              <TabContent value="funding" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <FundingRequirementsManager />
                </Suspense>
              </TabContent>

              <TabContent value="documents" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <DocumentManager />
                </Suspense>
              </TabContent>

              <TabContent value="sectors" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <SectorAllocationManager />
                </Suspense>
              </TabContent>

              <TabContent value="performance" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <PerformanceDataManager />
                </Suspense>
              </TabContent>

              <TabContent value="contacts" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <ContactMessageManager />
                </Suspense>
              </TabContent>

              <TabContent value="companies" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <CompanyManager />
                </Suspense>
              </TabContent>

              <TabContent value="users" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <UsersManager />
                </Suspense>
              </TabContent>

              <TabContent value="settings" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <SettingsManager />
                </Suspense>
              </TabContent>

              <TabContent value="tasks" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <KanbanBoard />
                </Suspense>
              </TabContent>

              <TabContent value="wizard" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <WizardInfoManager />
                </Suspense>
              </TabContent>

              <TabContent value="meetings" activeTab={activeTab}>
                <Suspense fallback={<div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>}>
                  <Card>
                    <CardHeader>
                      <CardTitle>Meeting Requests</CardTitle>
                      <CardDescription>
                        Manage and track all meeting requests from investors
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        </div>
                      ) : (
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date & Time</TableHead>
                                <TableHead>Business</TableHead>
                                <TableHead>Investor</TableHead>
                                <TableHead>Investment Amount</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {meetings.map((meeting) => (
                                <TableRow key={meeting.id}>
                                  <TableCell>
                                    <div className="font-medium">
                                      {format(new Date(meeting.meeting_date), 'MMM d, yyyy')}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {meeting.meeting_time}
                                    </div>
                                  </TableCell>
                                  <TableCell>{meeting.business?.business_name}</TableCell>
                                  <TableCell>
                                    <div>{meeting.full_name}</div>
                                    <div className="text-sm text-gray-500">{meeting.email}</div>
                                  </TableCell>
                                  <TableCell>
                                    ${meeting.investment_amount.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={getStatusBadgeColor(meeting.status)}>
                                      {meeting.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Select
                                      value={meeting.status}
                                      onValueChange={(value) => 
                                        updateMeetingStatus(meeting.id, value as Meeting['status'])
                                      }
                                    >
                                      <SelectTrigger className="w-[140px]">
                                        <SelectValue placeholder="Select status" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                        <SelectItem value="cancelled">Cancelled</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Suspense>
              </TabContent>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

// Wrap the export with error boundary and auth check
export default function AdminSettingsWrapper() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      }>
        <AdminSettings />
      </Suspense>
    </ErrorBoundary>
  );
}

